import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import I18n from 'i18n-js';

import {
  LOOTPACK_PURCHASE_PATH,
  useGoogleTagManagerPage,
} from '~/components/GoogleTagManager';
import LightTextInput from '~/components/LightTextInput';
import LootBoyButton from '~/components/LootBoyButton';
import BundleCampaignProvider from '~/components/LootPackViews/BundleSelector/BundleCampaignProvider';
import LootPacksNavigationBar from '~/components/LootPackViews/LootPacksNavigationBar';
import PopupContent from '~/components/PopupContent';
import SubscriptionRedirectPopup from '~/components/SubscriptionPopup/SubscriptionRedirectPopup';

import { INTERNAL_CURRENCIES } from '~/constants/payments';
import useBooleanState from '~/hooks/useBooleanState';
import { lastBundleAmountSelector } from '~/redux/selectors';
import { validateEmail } from '~/utils';

import BundleSelector from '../BundleSelector';
import ExchangeCurrencyDialog from '../ExchangeCurrencyDialog';
import { SIGNUM_TYPE, useLootPackHook } from '../shared';
import Disclaimer from './Disclaimer';
import PaymentMethodSelector from './PaymentMethodSelector';

import styleLootpacks from '../LootPackViews.module.scss';
import style from './PurchaseView.module.scss';

const getQuantity = (bundle) => Number((bundle || '').slice(1));

// eslint-disable-next-line complexity
const PurchaseView = () => {
  useGoogleTagManagerPage(LOOTPACK_PURCHASE_PATH);

  const location = useLocation();
  const history = useHistory();

  const lastBundleAmount = useSelector(lastBundleAmountSelector);
  const { repurchaseMode, bundleSelected: defaultBundle } =
    location.state || {};

  const [paymentMethod, setPaymentMethod] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [showSeasonTicketDialog, changeShowSeasonTicketDialog] =
    useBooleanState();

  const initialBundle =
    repurchaseMode && lastBundleAmount ? lastBundleAmount : defaultBundle;

  const {
    loading,
    _id,
    name,
    headline,
    key,
    subHeadline,
    acceptTicketPayment,
    ticketPayment,
    InfoMessage,
    bundleSelected,
    setBundleSelected,
    bundles,
    buttonSettings,
    lootId,
    newBalance,
    priceLabel,
    bonuses,
    currency,
    ticketBalance,
    userEmail,
    onPurchase,
    processing,
    price,
    canPurchase,
  } = useLootPackHook({
    initialBundle,
    paymentMethod,
    changeShowSeasonTicketDialog,
  });

  const [email, setEmail] = useState(userEmail || '');

  const {
    alertMessage,
    title = I18n.t('PurchaseView_button_buy'),
    validateEmail: validate,
    disabled,
  } = buttonSettings;

  const onPurchaseWrapped = () => {
    if (ticketPayment) {
      setShowDialog(true);
    } else if (paymentMethod?.type === SIGNUM_TYPE) {
      history.push({
        pathname: `/l/${_id}/signum`,
        state: {
          price,
          quantity: getQuantity(bundleSelected),
        },
      });
    } else {
      onPurchase(email);
    }
  };

  if (INTERNAL_CURRENCIES.includes(currency)) {
    return (
      <Redirect
        to={{
          pathname: `/loots/${lootId}`,
          state: {
            bundleSelected,
          },
        }}
      />
    );
  }

  const onClose = () => {
    history.replace(`/loots/${lootId}`);
  };

  return (
    <>
      <PopupContent
        className={style.purchaseView}
        loading={loading}
        onClose={onClose}
      >
        <div className={styleLootpacks.content}>
          <LootPacksNavigationBar
            onClose={onClose}
            item={{
              name,
              _id,
            }}
            itemName="loot"
            className={style.navigationBar}
          />
          <div className={style.header}>
            <h1>{!headline && !subHeadline ? name : headline}</h1>
            {subHeadline && <h2>{subHeadline}</h2>}
          </div>
          {InfoMessage ? (
            <InfoMessage />
          ) : (
            <div className={style.paymentDetails}>
              <div className={style.paymentSelector}>
                <BundleCampaignProvider
                  _id={_id}
                  currency={currency}
                  bundleSelected={bundleSelected}
                  className={style.purchaseTooltip}
                  lootBundles={bundles}
                >
                  <BundleSelector
                    bundles={bundles}
                    bonuses={bonuses}
                    bundleSelected={bundleSelected}
                    onSelectBundle={setBundleSelected}
                    className={style.bundleSelector}
                    showAmountHeader
                  />
                </BundleCampaignProvider>
                <h3>{I18n.t('PurchaseView_label_select_payment_method')}</h3>
                <PaymentMethodSelector
                  currency={currency}
                  methodSelected={paymentMethod}
                  acceptTicketPayment={
                    acceptTicketPayment && Boolean(ticketBalance)
                  }
                  onMethodSelected={setPaymentMethod}
                  quantity={getQuantity(bundleSelected)}
                />
              </div>
              <div className={style.purchaseConfirm}>
                <h3>{I18n.t('buy_tickets_cost')}</h3>
                <div
                  className={classNames(style.price, styleLootpacks.price, {
                    [currency?.toLowerCase() ?? '']: 1,
                    ticket: ticketPayment,
                  })}
                >
                  <span>{priceLabel}</span>
                  {!ticketPayment && (
                    <span>{I18n.t('LootDetailView_purchase_tax')}</span>
                  )}
                </div>
                {paymentMethod && paymentMethod.type === 'paymentwall' && (
                  <div className={style.emailInput}>
                    <p>{I18n.t('PurchaseView_email_prompt')}</p>
                    <LightTextInput
                      iconName="envelope"
                      onInputChanged={setEmail}
                      placeholder={I18n.t('PurchaseView_email_placeholder')}
                      type="email"
                      value={email}
                    />
                  </div>
                )}
                {alertMessage && (
                  <p className={style.currencyAlert}>{alertMessage}</p>
                )}
                <LootBoyButton
                  type="green"
                  className={style.purchaseButton}
                  disabled={
                    disabled ||
                    processing ||
                    !paymentMethod ||
                    (validate && !validateEmail(email)) ||
                    !canPurchase
                  }
                  onClick={onPurchaseWrapped}
                  title={title}
                />
                <Disclaimer
                  lootKey={key}
                  currency={currency}
                  name={name}
                  formattedPrice={priceLabel}
                  ticketPayment={ticketPayment}
                />
              </div>
            </div>
          )}
        </div>
      </PopupContent>
      <ExchangeCurrencyDialog
        onClose={() => setShowDialog(false)}
        show={showDialog}
        currency="ticket"
        price={priceLabel}
        lootId={lootId}
        bundleSelected={bundleSelected}
        balance={newBalance}
        onPurchase={onPurchase}
        processing={processing}
      />
      <SubscriptionRedirectPopup
        show={showSeasonTicketDialog}
        onClose={changeShowSeasonTicketDialog.setFalse}
      />
    </>
  );
};

export default PurchaseView;
